import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import {
  HomeIcon,
  CloudArrowUpIcon,
  ChatBubbleLeftRightIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  MagnifyingGlassIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UsersIcon,
  ChatBubbleBottomCenterIcon
} from '@heroicons/react/24/outline';
import { auth } from './firebase';

const Dashboard = ({ userData }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const isPaid = userData?.isPaid;
  const isAdmin = user && user.email === process.env.REACT_APP_ADMIN_EMAIL;

  const handleLogout = async () => {
    try {
      if (window.confirm("Deseja realmente sair da sua conta?")) {
        await signOut(auth);
        navigate('/');
      }
    } catch (err) {
      console.error('Erro ao fazer logout:', err);
    }
  };

  const menuItems = [
    { name: 'Feed de Aulas', path: '/dashboard/feed', icon: HomeIcon, show: true, type: 'link' },
    { name: 'Gestão de Alunos', path: '/dashboard/usuarios', icon: UsersIcon, show: isAdmin, type: 'link' },
    { name: 'Chamados Suporte', path: '/dashboard/suporte', icon: ChatBubbleBottomCenterIcon, show: isAdmin, type: 'link' },
    { name: 'Enviar Material', path: '/dashboard/upload', icon: CloudArrowUpIcon, show: isAdmin, type: 'link' },
    { name: 'Suporte Aluno', path: '/dashboard/contato', icon: ChatBubbleLeftRightIcon, show: true, type: 'link' },
    { name: 'Sair da Conta', icon: ArrowLeftOnRectangleIcon, show: true, type: 'action', onClick: handleLogout },
  ];

  return (
    <div className="flex h-screen bg-[#020617] text-gray-100 font-sans">
      {/* Sidebar Desktop */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-[#020617] border-r border-white/5 
        transition-transform duration-300 ease-in-out transform
        ${menuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
      `}>
        <div className="h-full flex flex-col p-6">
          <div className="flex items-center gap-3 px-2 mb-10 group cursor-pointer" onClick={() => navigate('/')}>
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/20 blur-lg rounded-full group-hover:bg-emerald-500/30 transition-all"></div>
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg relative z-10 border border-white/20">
                <CurrencyDollarIcon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="logo-traderflix text-xl">
              <span className="text-white">Trader</span>
              <span className="text-emerald-500 ml-0.5">Flix</span>
            </div>
          </div>

          <nav className="flex-1 space-y-2">
            {menuItems.filter(item => item.show).map((item) => {
              const isActive = item.type === 'link' && (location.pathname === item.path || (item.path === '/dashboard/feed' && location.pathname.startsWith('/dashboard/video/')));

              const itemClasses = `
                w-full flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-200 group
                ${isActive
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-[inset_0_0_15px_rgba(16,185,129,0.05)]'
                  : item.type === 'action'
                    ? 'text-red-400/70 hover:bg-red-500/10 hover:text-red-400 border border-transparent'
                    : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'}
              `;

              if (item.type === 'action') {
                return (
                  <button key={item.name} onClick={item.onClick} className={itemClasses}>
                    <item.icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-red-400/50 group-hover:text-red-400'}`} />
                    <span className="font-bold text-sm tracking-tight">{item.name}</span>
                  </button>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMenuOpen(false)}
                  className={itemClasses}
                >
                  <item.icon className={`w-5 h-5 ${isActive ? 'text-emerald-400' : 'text-gray-500 group-hover:text-gray-300'}`} />
                  <span className="font-bold text-sm tracking-tight">{item.name}</span>
                  {isActive && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]" />}
                </Link>
              );
            })}
          </nav>

          <div className="mt-auto p-4 bg-white/5 rounded-3xl border border-white/10 relative overflow-hidden group">
            <div className="flex items-center gap-3 relative z-10">
              <div className={`w-10 h-10 rounded-full border flex items-center justify-center font-black text-xs transition-all ${isPaid ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-500'
                }`}>
                {user?.email?.substring(0, 2).toUpperCase() || 'TR'}
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-black truncate text-white italic">{isAdmin ? 'ADMINISTRADOR' : (isPaid ? 'MEMBRO VIP' : 'AGUARDANDO PAGO')}</p>
                <p className="text-[10px] text-gray-500 uppercase tracking-widest font-black truncate max-w-[120px]">{user?.email}</p>
              </div>
              {isPaid && (
                <div className="ml-auto">
                  <ShieldCheckIcon className="w-5 h-5 text-emerald-500 animate-pulse" />
                </div>
              )}
            </div>
            {!isPaid && (
              <div className="absolute inset-0 bg-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
            )}
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-20 bg-[#020617]/80 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-8 z-40">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors"
            >
              {menuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>
            <div className="hidden md:flex items-center gap-3 bg-white/5 border border-white/10 px-4 py-2.5 rounded-2xl w-full max-w-md focus-within:border-emerald-500/30 transition-all font-sans">
              <MagnifyingGlassIcon className="w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Pesquisar..."
                className="bg-transparent border-none outline-none text-sm w-full placeholder:text-gray-600 font-medium"
              />
            </div>
          </div>

          <div className="flex items-center gap-6">
            <button className="relative p-2 text-gray-400 hover:text-white transition-colors">
              <BellIcon className="w-6 h-6" />
              <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-emerald-500 rounded-full border-2 border-[#020617]" />
            </button>
            <div className="h-8 w-px bg-white/10 hidden sm:block" />
            <div className="hidden sm:flex flex-col items-end">
              <p className="text-xs font-black text-emerald-500 uppercase tracking-tighter italic">TRADERFLIX CENTRAL</p>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest">{isPaid ? 'Acesso Liberado' : 'Acesso Restrito'}</p>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8 relative scrollbar-hide">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500/5 blur-[120px] -z-10 rounded-full pointer-events-none" />
          <div className="max-w-6xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {menuOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden" onClick={() => setMenuOpen(false)} />
      )}
    </div>
  );
};

export default Dashboard;
